import React from 'react';
import { useQuery } from '@apollo/client';
import { allStoreTypesQuery } from '../../graphql/queries';
import { Input } from 'reactstrap';
import { useState } from 'react';
import { allCapsToNormal } from '@/utils/stringHelper';
import { StoreType } from '../../graphql/generatedTypes';

type StoreTypeSelectorParams = {
  storeType: string;
  onChange: Function;
};

export enum VytalStoreTypes {
  Restaurant = 'RESTAURANT',
  Canteen = 'CANTEEN',
  KAM = 'KAM',
  ReturnBox = 'RETURN_BOX',
  Internal = 'INTERNAL',
  Event = 'EVENT',
  Venue = 'VENUE',
}

export const pickpackTypesByVytalType: { [key: string]: string[] } = {
  [VytalStoreTypes.Restaurant]: [
    StoreType.Restaurant,
    StoreType.SmallRestaurantChain,
    StoreType.Caterer,
    StoreType.LocalRetailer,
  ],
  [VytalStoreTypes.Canteen]: [
    StoreType.UniversityCanteen,
    StoreType.SelfOperatedCanteen,
    StoreType.NationalCateringCompany,
    StoreType.InternationalCateringCompany,
    StoreType.Hospital,
    StoreType.Campus,
  ],
  [VytalStoreTypes.KAM]: [
    StoreType.RetailChain,
    StoreType.OnlineRetailer,
    StoreType.NationalChain,
    StoreType.InternationalChain,
  ],
  [VytalStoreTypes.Event]: [
    StoreType.Stadium,
    StoreType.Festival,
    StoreType.Entertainment,
    StoreType.PublicEvents,
  ],
  [VytalStoreTypes.ReturnBox]: [
    StoreType.ReturnBox,
    StoreType.ServicedReturnBoxUnpaid,
    StoreType.ServicedReturnBoxPaid,
  ],
  [VytalStoreTypes.Internal]: [
    StoreType.Franchisee,
    StoreType.DemoStore,
    StoreType.Internal,
    StoreType.LostBowls,
    StoreType.Warehouse,
  ],
  [VytalStoreTypes.Venue]: [StoreType.Venue],
};

export const getAllPickpackTypes = () => {
  return Object.values(pickpackTypesByVytalType).reduce(
    (acc, val) => acc.concat(val),
    [],
  );
};

export default function StoreTypeSelector(params: StoreTypeSelectorParams) {
  const vytalTypeByPickpackType: { [key: string]: string } = {};

  Object.entries(pickpackTypesByVytalType).forEach(
    ([vytalType, pickpackTypes]) => {
      pickpackTypes.forEach((pickpackType) => {
        vytalTypeByPickpackType[pickpackType] = vytalType;
      });
    },
  );
  const { storeType: pickpackType, onChange } = params;

  const [vytalType, setVytalType] = useState<string>(
    vytalTypeByPickpackType[pickpackType] || VytalStoreTypes.Restaurant,
  );
  // use introspection query to fetch all store types
  useQuery(allStoreTypesQuery, {
    onCompleted: (data) => {
      const storeTypes = data.__type.enumValues.map(
        (storeType: any) => storeType.name,
      );
      for (const types of Object.values(pickpackTypesByVytalType)) {
        for (const type of types) {
          if (!storeTypes.includes(type)) {
            throw new Error(
              `Store type ${type} not present in pickpack! pickpack types: ${storeTypes.join(
                ', ',
              )}`,
            );
          }
        }
      }
    },
  });

  const getPickpackTypes = () => {
    const pickpackTypes = pickpackTypesByVytalType[vytalType] || [];
    if (!pickpackTypes.includes(pickpackType)) {
      pickpackTypes.push(pickpackType);
    }
    return pickpackTypes;
  };

  const updateVytalType = (newVytalType: string) => {
    const pickpackTypes = pickpackTypesByVytalType[newVytalType] || [];
    if (!pickpackTypes.includes(pickpackType)) {
      onChange(pickpackTypes[0] || pickpackType);
    }
    setVytalType(newVytalType);
  };

  return (
    <div>
      <div className="input-group mb-3">
        <span className="input-group-text" id="basic-addon1">
          Category
        </span>
        <Input
          type="select"
          className="form-control custom-select"
          name="vytal-store-type"
          onChange={(e) => updateVytalType(e.target.value)}
          value={vytalType}
          selected={vytalType}
        >
          {Object.values(VytalStoreTypes).map((type: string) => (
            <option key={type} value={type}>
              {allCapsToNormal(type)}
            </option>
          ))}
        </Input>
      </div>
      <div className="input-group mb-3">
        <span className="input-group-text" id="basic-addon1">
          Type
        </span>
        <Input
          type="select"
          className="form-control custom-select"
          name="pickpack-store-type"
          onChange={(e) => onChange(e.target.value)}
          value={pickpackType}
          selected={pickpackType}
        >
          {getPickpackTypes().map((type: string) => (
            <option key={type} value={type}>
              {allCapsToNormal(type)}
            </option>
          ))}
        </Input>
      </div>
    </div>
  );
}
